import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  systemPreference: Theme;
  isSystemPreference: boolean;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  useSystemPreference: () => void;
}

const THEME_STORAGE_KEY = 'center-app-theme-preference';

export const useThemeDetection = (): ThemeState => {
  const [systemPreference, setSystemPreference] = useState<Theme>(() => {
    // Detect initial system preference
    if (typeof window !== 'undefined' && globalThis.matchMedia) {
      return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  const [theme, setTheme] = useState<Theme>(() => {
    // Check for stored preference first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored as Theme;
      }
      if (stored === 'system') {
        return systemPreference;
      }
    }
    // Default to system preference
    return systemPreference;
  });

  const [isSystemPreference, setIsSystemPreference] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'system' || stored === null;
    }
    return true;
  });

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window !== 'undefined' && globalThis.matchMedia) {
      const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = (e: MediaQueryListEvent) => {
        const newSystemPreference = e.matches ? 'dark' : 'light';
        setSystemPreference(newSystemPreference);
        
        // If user is using system preference, update theme
        if (isSystemPreference) {
          setTheme(newSystemPreference);
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isSystemPreference]);

  // Apply theme to document
  useEffect(() => {
    if (typeof document !== 'undefined') {
      console.log(`🎨 Theme Detection: Applying ${theme} theme (system: ${systemPreference}, using system: ${isSystemPreference})`);
      
      // Remove existing theme attributes
      delete document.documentElement.dataset.theme;
      delete document.body.dataset.theme;

      // Apply new theme
      if (theme === 'dark') {
        document.documentElement.dataset.theme = 'dark';
        document.body.dataset.theme = 'dark';
      } else {
        // Light theme is default, but still set attribute for consistency
        document.documentElement.dataset.theme = 'light';
        document.body.dataset.theme = 'light';
      }
    }
  }, [theme, systemPreference, isSystemPreference]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setIsSystemPreference(false);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const setThemeManually = (newTheme: Theme) => {
    setTheme(newTheme);
    setIsSystemPreference(false);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const useSystemPreference = () => {
    setTheme(systemPreference);
    setIsSystemPreference(true);
    localStorage.setItem(THEME_STORAGE_KEY, 'system');
  };

  return {
    theme,
    systemPreference,
    isSystemPreference,
    toggleTheme,
    setTheme: setThemeManually,
    useSystemPreference,
  };
};